import React from 'react'
import { Grid, Typography, useMediaQuery } from '@mui/material'
import { compose } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { PropTranslations } from 'smartstream-widgets/types/widget'
import { CompetitorWithPredictedScore } from 'smartstream-widgets/widgets/PredictedScore/types'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) => createStyles({
  content: {
    maxWidth: 105
  },
  header: {
    borderBottom: '2px solid currentColor',
    color: theme.palette.text.primary,
    flexShrink: 0,
    flexWrap: 'nowrap',
    fontSize: 11,
    fontWeight: 'bold',
    lineHeight: 1.1,
    padding: `4px 0px 6px`,
    width: '100%'
  },
  liveScoresContainer: {
    paddingTop: 15,
    '&::before': {
      background: theme.palette.text.secondary,
      content: '\'\'',
      display: 'block',
      flex: 1,
      height: 1,
      margin: `0px 4px`,
      order: 1
    },
    '& *:last-child': {
      order: 2
    }
  },
  overflowEllipsis: {
    display: 'block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  scorePredicted: {
    fontSize: 42,
    fontWeight: 600,
    fontFamily: `${theme.typography.variantFontFamily}, ${theme.typography.fontFamily}`,
    lineHeight: 1.1
  },
  scoreLive: {
    color: 'inherit',
    fontWeight: 'bold'
  },
  scoreLiveCurrent: {
    fontSize: 16,
    fontFamily: `${theme.typography.variantFontFamily}, ${theme.typography.fontFamily}`
  },
  scoreLivePredicted: {
    fontSize: 24,
    fontFamily: `${theme.typography.variantFontFamily}, ${theme.typography.fontFamily}`,
    lineHeight: 1
  },
  scoreContainer: {
    position: 'relative'
  },
  scoreLabel: {
    bottom: '100%',
    color: 'inherit',
    fontSize: 7,
    fontWeight: 'bold',
    position: 'absolute',
    textTransform: 'uppercase'
  },
  scoreLabelLeft: {
    left: 0
  },
  scoreLabelRight: {
    right: 0
  },
  scoreResultLoser: {
    color: theme.palette.text.secondary
  },
  scoreResultWinner: {
    color: theme.palette.text.primary
  }
})

type Props = {
  alignRight: boolean,
  classes: {
    [K in string]: string
  },
  competitor: CompetitorWithPredictedScore,
  matchStatus: string,
  theme: Theme,
  translations: PropTranslations,
  winning: boolean
}

function SinglePredictedScore(props: Props) {
  const {
    alignRight = false,
    classes,
    competitor: {
      currentScore,
      brand: {
      } = {},
      displayName,
      officialName,
      firstName,
      secondName,
      predictedScore = 0
    },
    matchStatus,
    theme,
    translations,
    winning
  } = props
  const isLive = matchStatus === 'InPlay'
  const containerTextAlign = isLive ? (alignRight ? 'right' : 'left') : 'center'

  const below576 = useMediaQuery(theme.breakpoints.up('c576'))

  return (
    <Grid
      data-testid='single-predicted-score'
      container
      direction='column'
      wrap='nowrap' justifyContent='flex-end' style={{ textAlign: containerTextAlign }} className={classes.content}>
      <Typography data-testid='team-header' className={classes.header}>
        {
          !below576 && (firstName && secondName) ||  (displayName && displayName[0] && displayName[1])?
            <div data-testid='team-name' >
              <span className={classes.overflowEllipsis}>{firstName || displayName[0] }</span>
              <span className={classes.overflowEllipsis}>{secondName || displayName[1]}</span>
            </div>
            : officialName
        }
      </Typography>
      {isLive && predictedScore && (
        <Grid
          data-testid='live-scores-container'
          container alignItems='center'
          justifyContent='space-between'
          className={`${classes.liveScoresContainer} ${winning ? classes.scoreResultWinner : classes.scoreResultLoser}`}>
          <div data-testid='score-container' className={classes.scoreContainer}>
            <Typography className={`${classes.scoreLabel} ${classes.scoreLabelLeft}`}>
              {translations.liveLabelPredicted}
            </Typography>
            <Typography className={`${classes.scoreLive} ${classes.scoreLivePredicted}`}>
              {predictedScore.toFixed(0)}
            </Typography>
          </div>
          <div data-testid='score-container' className={classes.scoreContainer}>
            <Typography className={`${classes.scoreLabel} ${classes.scoreLabelRight}`}>
              {translations.liveLabelNow}
            </Typography>
            <Typography className={`${classes.scoreLive} ${classes.scoreLiveCurrent}`}>
              {currentScore}
            </Typography>
          </div>
        </Grid>
      )}
      {!isLive && predictedScore !== null && predictedScore >= 0 && (
        <Typography
          className={`${classes.scorePredicted} ${winning ? classes.scoreResultWinner : classes.scoreResultLoser}`}>
          {predictedScore.toFixed(0)}
        </Typography>
      )}
    </Grid>
  )
}

export default compose(
  withStyles(styles, { name: 'SinglePredictedScore', withTheme: true, defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      liveLabelNow: t('liveLabelNow.translation', {
        defaultValue: 'Now',
        context: 'Text to display as label for current score in live match state inside Predicted Score widget'
      }),
      liveLabelPredicted: t('liveLabelPredicted.translation', {
        defaultValue: 'Predicted',
        context: 'Text to display as label for predicted score in live match state inside Predicted Score widget'
      })
    }
  })
)(SinglePredictedScore)
